import { Card, Flex, Form, InputNumber, Modal, Select, Table } from 'antd';
import { BaseButton } from 'components/button/button-base';
import { Text, Title } from 'components/mvp-typography';
import { PurchaseLayout } from './_components/PurchaseLayout';

import { CRX_SUPPORT_EMAIL } from 'app/config';
import { ErrorListDisplay } from 'components/ErrorDisplay';
import { AssessmentType } from 'lib/documents/patient-assessment/patient-assessment';
import { useGetProductPriceDebounced } from 'lib/hooks/tanstack/use-getProductPrice';
import { useQueryErrorNotifier } from 'lib/hooks/use-query-error-notifier';
import { useErrorList } from 'lib/utils/useErrorList';
import { MessageCircle } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { paths } from 'routes';
import { ProgressBarTierMarkers } from './_components/progressbar-with-tiermarkers';
import { UnitPriceRenderer } from './_components/unit-price-renderer';
import { useCart } from './context-cart';
import { InternationalNumberFormatter } from './formatNumber';
import { formatProductName } from './formatProductName';
import { MAX_CUSTOM_QUANTITY } from './page-billing-buy-bulk-constants';

interface PreviewRow {
  label: AssessmentType;
  quantity: number;
  unitPrice: number;
  discount: number;
  total: number;
  currency: 'CAD' | 'USD';
}

const currencyFormatter = (currency: 'CAD' | 'USD') =>
  Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency
  });

export function useCurrencyFormatter(currency: 'CAD' | 'USD') {
  return useMemo(() => currencyFormatter(currency), [currency]);
}

export function BillingBuyBulkPage() {
  const history = useHistory();
  const billing = useCart();
  const [assessmentType, setAssessmentType] =
    useState<AssessmentType>('post-injury');

  // Track user's entered quantity. Default to 1 for calculations when no value is entered.
  const [quantityInputValue, setQuantityInputValue] = useState<number>(1);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);

  // displayQuantity and queryAmount are now just aliases for clarity, or can use quantityInputValue directly
  const displayQuantity = quantityInputValue;
  const queryAmount = quantityInputValue;
  const productPricingQuery = useGetProductPriceDebounced(
    {
      type: assessmentType,
      productType: 'bulk',
      quantity: queryAmount
    },
    {
      enabled: displayQuantity <= MAX_CUSTOM_QUANTITY
    }
  );

  /**
   * Build a single preview row from the currently entered values.
   * Always shows one row so table is never empty.
   */
  const previewRow: PreviewRow = useMemo(() => {
    if (!productPricingQuery.data)
      return {
        label: assessmentType,
        quantity: 0,
        unitPrice: 0,
        discount: 0,
        total: 0,
        currency: 'CAD'
      };
      
    const data = productPricingQuery.data.data;
    return {
      label: assessmentType,
      quantity: displayQuantity,
      unitPrice: data.unitPrice,
      discount: data.discount,
      total: data.totalPrice,
      currency: data.currency as any
    };
  }, [assessmentType, displayQuantity, productPricingQuery.data]);

  const errorList = useErrorList();
  useQueryErrorNotifier(productPricingQuery, errorList, 'Product information');

  function handleQuantityChange(value: number | string | null) {
    const numericValue = typeof value === 'string' ? parseInt(value) : value;
    const sanitizedValue =
      numericValue === null || isNaN(numericValue) || numericValue < 1
        ? 1
        : Math.round(numericValue);
    setQuantityInputValue(sanitizedValue);
  }

  function handleAddToCart() {
    if (displayQuantity <= 0) return;
    const pricePerUnit = productPricingQuery.data?.data.unitPrice ?? 0;
    billing.dispatch({
      type: 'ADD_TO_CART',
      payload: {
        type: assessmentType,
        quantity: displayQuantity,
        pricePerUnit: pricePerUnit,
        isBulk: true
      }
    });
    history.push({
      pathname: paths.clinician.billing.checkout(),
      search: location.search
    });
  }

  const columns = useMemo(
    () => [
      {
        dataIndex: 'label',
        title: 'Product Type',
        render: (label: string) => <Text>{formatProductName(label)}</Text>
      },
      {
        dataIndex: 'quantity',
        title: 'Quantity',
        render: (quantity: number) => (
          <Text>{InternationalNumberFormatter.format(quantity)}</Text>
        )
      },
      {
        title: 'Unit price',
        render: (_: unknown, record: PreviewRow) => (
          <UnitPriceRenderer
            unitPrice={record.unitPrice}
            isLoading={productPricingQuery.isLoading}
          />
        )
      },
      {
        title: 'Savings',
        render: (_: any, record: PreviewRow) => (
          <Text>{(record.discount * 100).toFixed(2)}%</Text>
        )
      },
      {
        dataIndex: 'total',
        title: 'Total',
        render: (total: number, record: PreviewRow) => (
          <Text strong>{currencyFormatter(record.currency).format(total)}</Text>
        )
      }
    ],
    [productPricingQuery.isLoading]
  );

  return (
    <PurchaseLayout title="Buy Assessments In Bulk">
      <Card>
        <Form>
          <Flex gap={16} vertical>
            {/* Assessment Type Selection */}
            <Form.Item label="Select product" required>
              <Select
                value={assessmentType}
                onChange={(value: AssessmentType) => setAssessmentType(value)}
              >
                <Select.Option value={'baseline'}>Baseline test</Select.Option>
                <Select.Option value={'post-injury'}>
                  Post-Injury test
                </Select.Option>
              </Select>
            </Form.Item>

            {/* Progress Bar + Tier Labels (Read-Only) */}
            <Text>Amount to purchase</Text>
            <div>
              <Flex justify="center">
                <Title>
                  {InternationalNumberFormatter.format(queryAmount)}
                </Title>
              </Flex>
              <Form.Item style={{ paddingLeft: '1rem', paddingRight: '2rem' }}>
                <ProgressBarTierMarkers amount={queryAmount} />
              </Form.Item>
            </div>

            {/* Quantity Input Field */}
            <Form.Item label="Quantity" required>
              <InputNumber
                style={{ width: '100%' }}
                min={1}
                // max={MAX_CUSTOM_QUANTITY}
                value={displayQuantity}
                onChange={value => handleQuantityChange(value)}
                placeholder="Enter desired quantity"
              />
            </Form.Item>

            {/* Table with current product info */}
            {displayQuantity <= MAX_CUSTOM_QUANTITY ? (
              <Table
                dataSource={[previewRow]}
                rowKey="label"
                size="small"
                pagination={false}
                columns={columns}
              />
            ) : (
              <Flex gap={16} align='center' vertical>
                <Text>A bulk discount may be available.</Text>
              </Flex>
            )}

            {/* Checkout Button */}
            <Flex gap={16} justify="center">
              {displayQuantity > MAX_CUSTOM_QUANTITY ? (
                <BaseButton
                  type="primary"
                  onClick={() => setIsBulkModalOpen(true)}
                >
                  <MessageCircle /> Contact customer support
                </BaseButton>
              ) : (
                <BaseButton
                  type="primary"
                  onClick={handleAddToCart}
                  disabled={displayQuantity <= 0 || previewRow.unitPrice === 0}
                >
                  Checkout
                </BaseButton>
              )}
            </Flex>

            {displayQuantity < MAX_CUSTOM_QUANTITY && (
              <Flex justify="end">
                <BaseButton
                  type="text"
                  onClick={() => setIsBulkModalOpen(true)}
                >
                  <MessageCircle /> Contact customer support
                </BaseButton>
              </Flex>
            )}

            {/* Error Display */}
            {errorList.hasErrors && (
              <ErrorListDisplay closeable errorList={errorList as any} />
            )}
          </Flex>
        </Form>
      </Card>

      <Modal
        title="Contact Support"
        open={isBulkModalOpen}
        onOk={() => setIsBulkModalOpen(false)}
        onCancel={() => setIsBulkModalOpen(false)}
        centered
        footer={[
          <BaseButton
            key="ok"
            type="primary"
            onClick={() => setIsBulkModalOpen(false)}
          >
            OK
          </BaseButton>
        ]}
      >
        <Text>
          A bulk discount may be available. Please contact <a href={`mailto:${CRX_SUPPORT_EMAIL}`}>{CRX_SUPPORT_EMAIL}</a>
        </Text>
      </Modal>
    </PurchaseLayout>
  );
}
